import { INews, ICountry, IParagraph, IUploadFile } from "./shared/types";
import { Paragraph } from "./Paragraph";
import { Base } from "./Base";
import { User } from "./User";

import { isNumeric, isString, isObject, isArray, 
         sortObjectArray, createDate, DateOfNMonthsBefore,
} from "@/common/utils";
import { D } from "@/common/filters";

export interface INewsContents {
  Photos?: IUploadFile[];
  Text?: string;
  Upload?: boolean;
};

export class NewsContents implements INewsContents {
  Upload: boolean = false;
  Photos: IUploadFile[] = [];
  Text: string = '';
  
  addPhotos(files:IUploadFile[]) {
    this.Photos = files.map((file) => {
      return file;
    });
  }

  removePhoto(file:IUploadFile) {
    this.Photos.forEach((f,i) => {
      if (f==file)
      this.Photos.splice(i,1);
    });
    this.Upload = false;
  };

  hasPdfFile() : boolean {
    let v = false;
    if (this.Photos && this.Photos.length>0) {
      for (let i=0; i<this.Photos.length && !v; i++) {
        v = !!this.Photos[i].name && !!this.Photos[i].name.match(/\.pdf$/i);
      }
    }
    return v;
  }

  isValid() : boolean {
    this.Text = (this.Text + '').trim();
    return this.hasPdfFile() || !!this.Text; //(!!this.Text && !!this.Photos && this.Photos.length>0);
  }

  upload(userCode:string, newsId:number) {
    if (this.isValid()) {
      this.Upload = false;
      setTimeout(() => {
        this.Photos[0].properties = {
          news_id: newsId,
          text: this.Text,
          user_fk: userCode,
          media_path: this.Photos[0].name
        }
        this.Upload = true;
      }, 0)
    }
  }
}

export class News extends Base implements INews {
  HalloId?: string; //system generated surrogate key.
  Code?: string; //application defined key, define once and never changed
  Name?: string; //display name, such as nickname or short name
  Catchword?: string;  //a message describing characteritics and an entity
  Avatar?: string;
  CoverImage?: string;
  Region?: ICountry;
  Email?: string;
  Mobile?: string;
  QRCode?: string;
  
  DateStart?: Date;
  DateEnd?: Date;
  Contents: Paragraph[] = [];

  ListIndex?: number; //used in listview for display purpose
  shown?: boolean = false;
  isNew?: boolean = false;

  protected _propertyNames = [
    'HalloId', 'Code','Name', 'Catchword', 'Avatar', 'CoverImage', 'Region', 
    'Email', 'Mobile', 'QRCode', 'Contents'
  ];

  
  constructor(news?:INews) {
    super();
    if (news) {
      this.Properties = news;
    }
  }

  get Properties() : INews {
    return <INews>this.getProperties();
  }

  set Properties(news: INews) {
    for (let p in news) {
      if (p=="Contents") {
        this[p] = [];
        news[p].forEach((paragraph) => {
          let c:Paragraph = new Paragraph(paragraph);
          this[p].push(c);
        }, this);
      } else if (this._propertyNames.indexOf(p) != -1) {
        this[p] = news[p];
      }
    }
  }

  set PropertiesFromStorage(news:any) {
    if (news.id && news.id!=this.HalloId) {
      this.HalloId = news.id;
    }
    if (news.title && news.title!=this.Name) {
      this.Name = news.title.replace(/\\"/g,'"');
    }
    if (news.date_begin) {
      this.DateStart = createDate(news.date_begin);
    } else {
      this.DateStart = null;
    }
    if (news.date_end) {
      this.DateEnd = createDate(news.date_end);
    } else {
      this.DateEnd = this.DateEnd || this.DateStart || null;
    }
    this.DateStart = this.DateStart || this.DateEnd || null;
    if (news.contents) {
      this.Contents = news.contents.map((paragraph:IParagraph) => {
        let c = new Paragraph();
        c.PropertiesFromStorage = paragraph;
        return c;
      });
    }
    if (news.is_new!=undefined) {
      this.isNew = news.is_new;
    } else {
      let ds = this.DateStart;
      if (ds) {
        let dd = DateOfNMonthsBefore(1, new Date());
        dd.setHours(0,0,0,0);
        ds.setHours(0,0,0,0);
        this.isNew = (ds.getTime() >= dd.getTime());
      }
    }
  }

  hasContents() : boolean {
    return this.Contents && this.Contents.length>0;
  }

  hasValidInput() : boolean {
    this.Name = this.Name ? this.Name.trim() : null;
    return !!this.Name && (!!this.DateStart || !!this.DateEnd);
  }

  get HtmlName() : string {
    return !this.Name ? null : this.Name.replace(/\\n/g,'<br/>');
  }

  get EditName() : string {
    return !this.Name ? null : this.Name.replace(/\\n/g,'\n');
  }

  get DateString() : string {
    let ds:string;
    let de:string;
    let dds:number, mms:number, yyyys:number;
    let dde:number, mme:number, yyyye:number;
    if (this.DateStart && this.DateEnd && 
      this.DateStart.getTime()!=this.DateEnd.getTime()) {
      dds = this.DateStart.getDate();
      mms = this.DateStart.getMonth();
      yyyys = this.DateStart.getFullYear();
      ds = D(this.DateStart,'dd MMM yyyy');
      dde = this.DateEnd.getDate();
      mme = this.DateEnd.getMonth();
      yyyye = this.DateEnd.getFullYear();
      de = D(this.DateEnd, 'dd MMM yyyy');
      if (yyyys==yyyye) {
        if (mms==mme) {
          ds = ds.split(' ')[0] + '-' + de;
        } else {
          ds = ds.substr(0,ds.length-5) + ' - ' + de;
        }
      } else {
        ds += ' - ' + de;
      }
    } else if (this.DateStart) {
      ds = D(this.DateStart,'dd MMM yyyy');
    } else if (this.DateEnd) {
      ds = D(this.DateEnd,'dd MMM yyyy');
    } else if (!this.HalloId) {
      ds = 'เลือกช่วงวันที่กิจกรรม';
    } else {
      ds = '-';
    }

    return ds;
  }

  async getContents(user:User) {
    try {
      let data = await user.requestResource('mis/news_photos/' + this.HalloId);
      this.Contents = data.map((p:Paragraph) => {
        let c = new Paragraph();
        c.PropertiesFromStorage = p;
        return c;
      });
    } catch (error) {
    }
  }

  async save(user:User) {
    try {
      let data:any = {
        title: this.Name,
        date_begin: this.DateStart,
        date_end: this.DateEnd,
        user_fk: user.Code
      }
      data = await user.requestResource('mis/news','POST',data);
      this.HalloId = data.id;
    } catch (error) {
      throw error;
    }
  }
}